// modules
import { useEffect, useMemo } from "react";
import type { RouteObject } from "react-router-dom";
import { useLocation, useRoutes } from "react-router-dom";

// global styles
import "App.css";

// constants
import { ProtectedRoutesMap, protectedRoutesMap, RouteItem, routes } from "constants/routes";

// component
import RouteGuard from "app/hooks/RouteGuard";

// redux
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";
import { getLoggedInUserInfo, getUserAuthDetails, updateUserToken, authenticationInProgress } from "redux/userList/userSlice";

// utils
import { getCookie } from "utils/token";

function filterRoutes(_routes: RouteObject[], _permissions: string[], _routeMap: { [x: string]: RouteItem }) {
	const routeItems: RouteItem[] = [];

	_permissions.forEach((permission: string) => {
		const routeObj = _routeMap[permission as keyof ProtectedRoutesMap];

		// strict boolean check to avoid false positive scenario
		if (routeObj && routeObj?.hide !== true) {
			routeItems.push(routeObj);
		}
	});

	const protectedRoutes = routeItems.map((item) => {
		const routeComponent: RouteObject = {
			path: item?.path
		};
		if (item?.protected) {
			routeComponent.element = <RouteGuard>{item?.element}</RouteGuard>;
		}
		if (item?.children && item?.protected) {
			routeComponent.children = item?.children?.map((child) => ({
				path: child?.path,
				element: <RouteGuard>{child?.element}</RouteGuard>
			}));
		}

		return routeComponent;
	});

	return [...protectedRoutes, ...routes];
}

const useApp = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();

	const cookie = getCookie("USER_INFO_COOKIE");
	const loggedInUserData = useAppSelector(getLoggedInUserInfo);
	const authInProgress = useAppSelector(authenticationInProgress);
	const { data: loggedInUserDetails } = loggedInUserData || [];

	const { tenant_code, tenant_name } = useMemo(() => {
		return loggedInUserDetails?.[0] || {};
	}, [loggedInUserDetails]);

	const isAdminUser = !tenant_code && !tenant_name;

	// Get route map based on user type admin/tenant
	const routeMap = isAdminUser ? protectedRoutesMap?.admin : protectedRoutesMap?.tenant;

	/**
	 * @description: If valid cookies are present and user details are missing.
	 * Fetch the user details.
	 * This check will happen every change of location.
	 */
	useEffect(() => {
		if (cookie && !loggedInUserDetails && !authInProgress) {
			const tokenDetails = JSON.parse(cookie);
			dispatch(updateUserToken(tokenDetails));
			dispatch(getUserAuthDetails());
		}
	}, [location]);

	const permissions = useMemo(() => {
		const groups = loggedInUserDetails?.[0].groups || [];
		const addedPermissions: string[] = [];

		groups?.forEach((group: { permissions: Array<{ perm_code: string }> }) => {
			const groupPermissions = group?.permissions || [];
			groupPermissions?.forEach((permission) => {
				addedPermissions.push(permission?.perm_code);
			});
		});
		return addedPermissions;
	}, [loggedInUserDetails]);

	/**
	 * @description: Check each protected route against permissions and create a route object using routeMap data.
	 * Return routes only for which user have permission added in permissions list.
	 */
	const routesToRender = useMemo(() => {
		return filterRoutes(routes, permissions, routeMap);
	}, [routes, permissions, routeMap]);

	return {
		routesToRender
	};
};

function App() {
	const { routesToRender } = useApp();

	const routesElement = useRoutes(routesToRender);

	return <>{routesElement}</>;
}

export default App;
